import { createFileRoute } from '@tanstack/react-router';
import { Flex } from 'antd';
import { type Dayjs } from 'dayjs';
import { AlertCircle, LayoutList } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import {
  ActivityActorTypeFilterDropdown,
  ActivityCreatedFilterDropdown,
  ActivityEntityTypeFilterDropdown,
  ActivityOrganizationFilterDropdown,
  SuperadminActivityDetailDrawer,
  SuperadminActivityTable,
  type SuperadminActivityTableRow,
  useSuperadminActivityInfiniteQuery,
} from '../../../modules/superadmin';
import {
  ActivityFilterParamsSchema,
  parseOptionalActorType,
  parseOptionalEntityType,
} from '../../../modules/superadmin/utils/superadminActivityFilters';
import { NavbarTabsOverflow } from '../../../ui/components/navbar-tabs';
import { TableSurface } from '../../../ui/components/table-surface';
import { useLayoutInsets } from '../../../ui/providers/layout-insets';


const ActivityRoute = () => {
  const navigate = Route.useNavigate();
  const insets = useLayoutInsets();
  const search = Route.useSearch({ structuralSharing: false });
  const [selectedEvent, setSelectedEvent] = useState<SuperadminActivityTableRow | null>(null);

  const {
    entityType,
    entityId,
    actorType,
    actorId,
    organizationId,
    dateTimeFrom,
    dateTimeTo,
    status,
  } = search;

  const listParams = useMemo(
    () => ({
      entityType: entityType || undefined,
      entityId: entityId || undefined,
      actorType: actorType || undefined,
      actorId: actorId || undefined,
      organizationId: organizationId || undefined,
      dateTimeFrom: dateTimeFrom || undefined,
      dateTimeTo: dateTimeTo || undefined,
      status,
    }),
    [entityType, entityId, actorType, actorId, organizationId, dateTimeFrom, dateTimeTo, status],
  );

  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuperadminActivityInfiniteQuery(listParams);

  const items = useMemo(() => data?.pages.flatMap((p) => p.data.items) ?? [], [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleEntityTypeChange = useCallback(
    (value: string | undefined) => {
      void navigate({
        to: '/superadmin/activity',
        search: (prev) => ({
          ...prev,
          entityType: parseOptionalEntityType(value),
          entityId: undefined,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const handleActorTypeChange = useCallback(
    (value: string | undefined) => {
      void navigate({
        to: '/superadmin/activity',
        search: (prev) => ({
          ...prev,
          actorType: parseOptionalActorType(value),
          actorId: undefined,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const handleOrganizationChange = useCallback(
    (value: string | undefined) => {
      void navigate({
        to: '/superadmin/activity',
        search: (prev) => ({
          ...prev,
          organizationId: value,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const handleCreatedRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      void navigate({
        to: '/superadmin/activity',
        search: (prev) => ({
          ...prev,
          dateTimeFrom: dates?.[0]?.toISOString() ?? undefined,
          dateTimeTo: dates?.[1]?.toISOString() ?? undefined,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const statusOptions = [
    { id: 'all', name: 'All', icon: <LayoutList size={12} /> },
    { id: 'failed', name: 'Failed', icon: <AlertCircle size={12} /> },
  ];

  return (
    <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)`, minHeight: 0 }}>
      <Flex align="flex-end" style={{ padding: '0 24px', minHeight: 44 }}>
        <NavbarTabsOverflow
          title="MORE STATUSES"
          items={statusOptions}
          activeId={status ?? 'all'}
          onSelect={(option) =>
            navigate({
              to: '/superadmin/activity',
              search: (prev) => ({ ...prev, status: option.id === 'failed' ? 'failed' : undefined }),
              resetScroll: false,
            })
          }
        />
      </Flex>
      <TableSurface className="table-surface--page-inset-top-nav">
        <SuperadminActivityTable
          dataSource={items}
          isPending={isPending}
          isError={isError}
          onLoadMore={handleLoadMore}
          onShowDetails={setSelectedEvent}
          entityTypeFilterHeader={
            <ActivityEntityTypeFilterDropdown onChange={handleEntityTypeChange} />
          }
          actorTypeFilterHeader={<ActivityActorTypeFilterDropdown onChange={handleActorTypeChange} />}
          organizationFilterHeader={
            <ActivityOrganizationFilterDropdown onChange={handleOrganizationChange} />
          }
          createdFilterHeader={
            <ActivityCreatedFilterDropdown onRangeChange={handleCreatedRangeChange} />
          }
        />
      </TableSurface>
      <SuperadminActivityDetailDrawer
        open={!!selectedEvent}
        event={selectedEvent}
        onClose={() => {
          setSelectedEvent(null);
        }}
      />
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/superadmin/activity')({
  component: ActivityRoute,
  validateSearch: (params) => ActivityFilterParamsSchema.parse(params),
});
